import React, { useEffect, useState, useRef } from 'react';
import logger from '../utils/Logger.js';
import HealthMonitor from '../services/HealthMonitor.js';
import HealthWatchdog from '../services/HealthWatchdog.js';
import useFullscreen from '../hooks/useFullscreen';
import {
  countLoopManifestAssets,
  loadLoopManifestCache,
  saveLoopManifestCache
} from '../services/LoopManifestCache';
import {
  getLoopMediaCacheStatus,
  hydrateLoopMediaFromCache,
  prefetchLoopMediaAssets
} from '../services/LoopMediaPrefetch';
import {
  flushOfflineTelemetryQueue,
  getQueuedTelemetryCount,
  removeQueuedTelemetryEvents,
  sendBrowserSignalWithOfflineQueue
} from '../services/OfflineTelemetryQueue';
import {
  flushNativeTelemetryQueue,
  putNativePlaybackState,
  readNativeLoopOfflineStatus
} from '../services/NativeLoopOfflineBridge';
import { selectCreativeForSegmentRotation } from '../overlay/loop/creativeRotationSelector';
import { isLoopResidualImaCanaryEnabled } from '../overlay/core/PlaybackProfile';
import { getVideoOpacity } from '../overlay/stream/persistentUploadedVideoElement';
import resolveRuntimeAssetUrl from '../utils/runtimeAssetUrl';
import {
  buildYouTubeEmbedUrl,
  extractYouTubePlayerErrorCode,
  isBrightSignRuntime,
  isYouTubePlayerErrorMessage,
  YOUTUBE_IFRAME_ALLOW,
  YOUTUBE_REFERRER_POLICY
} from '../utils/youtubeEmbed';
import YouTubePlayerBridge from '../overlay/stream/youtubePlayerBridge';

const {
  markYouTubePlayerReady,
  postYouTubeListening,
  postYouTubePlayerCommand
} = YouTubePlayerBridge;

const CONTENT_BOOTSTRAP_TIMEOUT_MS = 15000;
const CONTENT_MONITORING_TIMEOUT_MS = 15000;
const BOOTSTRAP_SIGNAL_THROTTLE_MS = 30000;
const OFFLINE_TELEMETRY_FLUSH_BATCH_SIZE = 4;
const OFFLINE_TELEMETRY_FLUSH_RETRY_MS = 5000;
const TRANSPARENT_VIDEO_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const getPairedDeviceLabel = (userAgent = '') => {
  const ua = String(userAgent || '');

  if (/AFT[A-Z]|Fire TV/i.test(ua)) return 'Fire TV device';
  if (/Tizen/i.test(ua)) return 'Samsung TV';
  if (/webOS|Web0S|NetCast/i.test(ua)) return 'LG TV';
  if (/BrightSign/i.test(ua)) return 'BrightSign player';
  if (/Roku/i.test(ua)) return 'Roku device';
  if (/CrKey|Chromecast/i.test(ua)) return 'Chromecast device';
  if (/AppleTV/i.test(ua)) return 'Apple TV';

  return 'display';
};

export const getKioskContentObjectFit = () => {
  if (typeof window === 'undefined') return 'cover';

  const runtimeProfile = window.__TRILLBOARDS_DISPLAY_PROFILE;
  if (runtimeProfile?.name === 'ultrawide_bar') return 'contain';

  const width = Number(window.innerWidth || 0);
  const height = Number(window.innerHeight || 0);
  if (height > 0 && width / height >= 4 && height <= 480) return 'contain';

  return 'cover';
};

const hasPrefetchedYouTubeStream = (content) =>
  Array.isArray(content?.all_default_streams)
  && content.all_default_streams.some((stream) => {
    const streamContent = stream?.content || stream;
    const streamType = String(streamContent?.stream_type || streamContent?.streamType || stream?.stream_type || stream?.streamType || '').trim().toLowerCase();
    return streamType === 'youtube'
      && Boolean(streamContent?.youtube_url || streamContent?.youtubeUrl || streamContent?.youtube_id || streamContent?.youtubeId);
  });

const extractYouTubeVideoId = (url) => {
  if (typeof url !== 'string') return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match?.[1] || null;
};

const resolveInitialYouTubeVideoId = (content) => {
  const backgroundUrl = content?.background_stream?.content?.youtube_url
    || content?.background_stream?.content?.youtubeUrl;
  const backgroundVideoId = extractYouTubeVideoId(backgroundUrl);
  if (backgroundVideoId) {
    return backgroundVideoId;
  }

  const streams = Array.isArray(content?.all_default_streams) ? content.all_default_streams : [];
  for (const stream of streams) {
    const streamContent = stream?.content || stream;
    const streamType = String(streamContent?.stream_type || streamContent?.streamType || stream?.stream_type || stream?.streamType || '').trim().toLowerCase();
    if (streamType !== 'youtube') continue;
    const videoId = extractYouTubeVideoId(
      streamContent?.youtube_url || streamContent?.youtubeUrl
    );
    if (videoId) {
      return videoId;
    }
  }

  return null;
};

const resolveShellVideoId = (content, shellVideoId) => {
  const initialVideoId = resolveInitialYouTubeVideoId(content);
  if (initialVideoId) {
    return initialVideoId;
  }

  return hasPrefetchedYouTubeStream(content) ? shellVideoId : null;
};

const normalizeStartSeconds = (value) => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
};

const getStreamContent = (stream) => stream?.content || stream || {};

const getStreamId = (stream) => {
  const streamContent = getStreamContent(stream);
  return streamContent?.id || streamContent?._id || stream?.id || stream?._id || null;
};

const findStreamById = (streams, streamId) => {
  if (!streamId || !Array.isArray(streams)) return null;
  return streams.find((stream) => getStreamId(stream) === streamId) || null;
};

const resolvePlaybackStateStartSeconds = (content, videoId) => {
  if (!content || !videoId) return 0;

  const playbackState = content.playback_state;
  const positionSeconds = normalizeStartSeconds(playbackState?.position_seconds);
  if (!positionSeconds) return 0;

  const streams = Array.isArray(content.all_default_streams) ? content.all_default_streams : [];
  const streamId = playbackState?.stream_id;
  const stateStream = findStreamById(streams, streamId);
  const stateStreamVideoId = extractYouTubeVideoId(
    getStreamContent(stateStream)?.youtube_url || getStreamContent(stateStream)?.youtubeUrl
  );
  if (stateStreamVideoId) {
    return stateStreamVideoId === videoId ? positionSeconds : 0;
  }

  const backgroundContent = content.background_stream?.content || content.background_stream || {};
  const backgroundVideoId = extractYouTubeVideoId(backgroundContent.youtube_url || backgroundContent.youtubeUrl);
  const backgroundStreamId = backgroundContent.id || backgroundContent._id || null;
  if ((streamId && backgroundStreamId && streamId !== backgroundStreamId) || backgroundVideoId !== videoId) {
    return 0;
  }

  return positionSeconds;
};

const resolveShellPlayback = (content, shellVideoId) => {
  const videoId = resolveShellVideoId(content, shellVideoId);
  return {
    videoId,
    startSeconds: resolvePlaybackStateStartSeconds(content, videoId)
  };
};

const resolveInitialShellMedia = (content) => {
  const streams = [
    content?.background_stream,
    ...(Array.isArray(content?.all_default_streams) ? content.all_default_streams : [])
  ].filter(Boolean);

  for (const stream of streams) {
    const streamContent = getStreamContent(stream);
    const streamType = String(streamContent?.stream_type || streamContent?.streamType || '').trim().toLowerCase();
    const imageUrl = Array.isArray(streamContent?.images) ? streamContent.images.find(Boolean) : null;
    const videoUrl = streamContent?.video_url || streamContent?.videoUrl || streamContent?.local_file_path || streamContent?.localFilePath;

    if (streamType === 'images' && imageUrl) {
      return {
        type: 'image',
        url: imageUrl,
        name: streamContent?.name || 'Image stream',
      };
    }

    if ((streamType === 'uploaded' || streamType === 'local') && videoUrl) {
      return {
        type: 'video',
        url: videoUrl,
        name: streamContent?.name || 'Video stream',
      };
    }
  }

  return null;
};

const resolveSegmentCreative = (segment, selectedCreative) => {
  const resolved = segment?.resolved_creative || segment?.resolvedCreative || null;
  const resolvedUrl = resolved?.url
    || resolved?.asset_url
    || resolved?.assetUrl
    || resolved?.video
    || resolved?.video_url
    || resolved?.image_url
    || resolved?.media?.url
    || resolved?.asset?.url
    || null;

  if (resolvedUrl) {
    return {
      reservation_id: resolved?.placement_id || segment?.creative_rotation?.reservation_id || null,
      creative_id: resolved?.ad_id || resolved?.creative_id || resolved?.id || null,
      creative_source: resolved?.source || resolved?.creative_source || segment?.fill_source || null,
      asset_url: resolvedUrl,
      original_asset_url: resolved?._trillboards_original_asset_url || resolved?.original_asset_url || resolvedUrl,
      media_type: resolved?.media_type || resolved?.mediaType || null,
      rotation_policy: null,
      candidate_count: 0,
      eligible_candidate_count: 0,
      rotation_order: null,
      used_default_fallback: false,
      selection_source: 'resolved_creative',
    };
  }

  if (!selectedCreative) return null;
  return {
    ...selectedCreative,
    selection_source: 'creative_rotation',
  };
};

// Legacy loop manifests remain authoritative for every existing player. The
// server-resolved v2 projection is intentionally admitted only for the runtime
// canary: it is additive on ordinary interval screens, and treating it as a loop
// globally caused the #9624 YouTube teardown/restart regression.
//
// A native/legacy loop player that has both shapes keeps the legacy path. The v2
// fallback therefore changes only canary screens (currently Omen Chrome) whose
// shell lacks the legacy loop plan while the overlay already consumes v2.
const resolveShellLoopManifest = (content) => {
  const legacyPolicy = content?.playback_policy;
  const legacyLoopPlan = legacyPolicy?.schedule_policy?.source === 'loop_plan'
    ? legacyPolicy?.loop_plan
    : null;
  if (
    legacyPolicy?.delivery_mode === 'loop_composer'
    && Array.isArray(legacyLoopPlan?.segments)
    && legacyLoopPlan.segments.length > 0
  ) {
    return {
      policy: legacyPolicy,
      loopPlan: legacyLoopPlan,
      source: 'playback_policy',
    };
  }

  const resolvedPolicy = content?.resolved_loop_plan_v2;
  const resolvedLoopPlan = resolvedPolicy?.schedule_policy?.source === 'loop_plan'
    ? resolvedPolicy?.loop_plan
    : null;
  if (
    isLoopResidualImaCanaryEnabled(content)
    && resolvedPolicy?.delivery_mode === 'loop_composer'
    && Array.isArray(resolvedLoopPlan?.segments)
    && resolvedLoopPlan.segments.length > 0
  ) {
    return {
      policy: resolvedPolicy,
      loopPlan: resolvedLoopPlan,
      source: 'resolved_loop_plan_v2',
    };
  }

  return null;
};

const resolveShellLoopNowMs = (manifestSource, nowMs) => {
  const localNowMs = Number.isFinite(nowMs) ? Number(nowMs) : Date.now();
  if (manifestSource !== 'resolved_loop_plan_v2') {
    return localNowMs;
  }

  try {
    const clockOffset = Number(
      typeof window !== 'undefined'
        ? window.timeSyncManager?.clockOffset
        : 0
    );
    return Number.isFinite(clockOffset) ? localNowMs + clockOffset : localNowMs;
  } catch (_) {
    return localNowMs;
  }
};

const resolveActiveLoopState = (content, nowMs) => {
  const manifest = resolveShellLoopManifest(content);
  const resolvedNowMs = resolveShellLoopNowMs(manifest?.source, nowMs);
  const loopPlan = manifest?.loopPlan;
  const segments = Array.isArray(loopPlan?.segments) ? [...loopPlan.segments] : [];
  const loopDurationMs = Number(loopPlan?.loop_duration_ms) || Number(loopPlan?.loop_duration_seconds) * 1000;
  const anchorMs = Number(loopPlan?.ntp_epoch_anchor_ms) || resolvedNowMs;

  if (!segments.length || !Number.isFinite(loopDurationMs) || loopDurationMs <= 0) {
    return null;
  }

  segments.sort((a, b) => Number(a?.slot_index || 0) - Number(b?.slot_index || 0));
  let elapsedMs = (resolvedNowMs - anchorMs) % loopDurationMs;
  if (elapsedMs < 0) elapsedMs += loopDurationMs;

  const active = segments.find((segment) => {
    const offsetMs = Number(segment?.offset_ms) || 0;
    const durationMs = Number(segment?.duration_ms) || 0;
    return elapsedMs >= offsetMs && elapsedMs < offsetMs + durationMs;
  }) || segments[segments.length - 1];

  const activeOffsetMs = Number(active?.offset_ms) || 0;
  const activeDurationMs = Number(active?.duration_ms) || loopDurationMs;
  const cycleNumber = Math.max(0, Math.floor((resolvedNowMs - anchorMs) / loopDurationMs));
  const selectedCreative = selectCreativeForSegmentRotation(active, {
    cycleNumber,
    at: new Date(resolvedNowMs),
  });
  const activeCreative = resolveSegmentCreative(active, selectedCreative);

  return {
    plan_id: loopPlan?.plan_id ?? null,
    loop_manifest_source: manifest?.source || null,
    cycle_number: cycleNumber,
    slot_index: active?.slot_index ?? null,
    slot_offset_ms: Math.round(activeOffsetMs),
    slot_duration_ms: Math.round(activeDurationMs),
    tier: active?.tier || null,
    creative_class: active?.creative_class || null,
    fill_source: active?.fill_source || null,
    reservation_id: activeCreative?.reservation_id || active?.creative_rotation?.reservation_id || null,
    selected_creative_id: activeCreative?.creative_id || null,
    selected_creative_source: activeCreative?.creative_source || null,
    selected_creative_asset_url: activeCreative?.asset_url || null,
    selected_creative_original_asset_url: activeCreative?.original_asset_url || activeCreative?.asset_url || null,
    selected_creative_media_type: activeCreative?.media_type || null,
    selected_creative_selection_source: activeCreative?.selection_source || null,
    creative_rotation_policy: activeCreative?.rotation_policy || null,
    creative_rotation_candidate_count: activeCreative?.candidate_count ?? 0,
    creative_rotation_eligible_count: activeCreative?.eligible_candidate_count ?? 0,
    creative_rotation_order: activeCreative?.rotation_order ?? null,
    creative_rotation_used_default_fallback: activeCreative?.used_default_fallback === true,
    strict_loop_surface: isLoopResidualImaCanaryEnabled(content),
    elapsed_ms: Math.round(elapsedMs),
    remaining_ms: Math.max(250, Math.round(activeOffsetMs + activeDurationMs - elapsedMs)),
    loop_duration_ms: loopDurationMs,
    updated_at_ms: resolvedNowMs,
  };
};

const hasLoopComposerPlayback = (content) => Boolean(resolveShellLoopManifest(content));

const getContentAvailability = (content) => {
  const hasStreams = content?.all_default_streams?.length > 0;
  const hasAds = content?.overlay_advertisements?.length > 0;
  const hasLBar = content?.l_bar_content?.enabled && ['l-bar', 'lbar'].includes(String(content?.display_preferences?.display_mode || '').trim().toLowerCase());
  const hasProgrammatic = content?.header_bidding_settings?.enabled || content?.floodgates_settings?.enabled;
  const hasLoopComposer = hasLoopComposerPlayback(content);

  return {
    hasStreams,
    hasAds,
    hasLBar,
    hasProgrammatic,
    hasLoopComposer,
    hasDisplayableContent: hasStreams || hasAds || hasLBar,
  };
};

const getNavigatorOnlineState = () => {
  try {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'offline';
    }
  } catch (_) {}
  return 'online';
};

const normalizeCacheStatusDetails = (details = {}) => ({
  online_state: details.online_state || getNavigatorOnlineState(),
  using_cached_manifest: Boolean(details.using_cached_manifest),
  cached_manifest_age_ms: details.cached_manifest_age_ms ?? null,
  cached_asset_count: Number.isFinite(Number(details.cached_asset_count))
    ? Number(details.cached_asset_count)
    : 0,
  queued_event_count: Number.isFinite(Number(details.queued_event_count))
    ? Number(details.queued_event_count)
    : 0,
  ...(details.native_bridge_available !== undefined ? {
    native_bridge_available: Boolean(details.native_bridge_available),
  } : {}),
  ...(details.using_native_manifest !== undefined ? {
    using_native_manifest: Boolean(details.using_native_manifest),
  } : {}),
  ...(details.native_manifest_age_ms !== undefined ? {
    native_manifest_age_ms: details.native_manifest_age_ms,
  } : {}),
  ...(details.native_cached_asset_count !== undefined ? {
    native_cached_asset_count: Number(details.native_cached_asset_count) || 0,
  } : {}),
  ...(details.native_required_asset_count !== undefined ? {
    native_required_asset_count: Number(details.native_required_asset_count) || 0,
  } : {}),
  ...(details.native_queued_event_count !== undefined ? {
    native_queued_event_count: Number(details.native_queued_event_count) || 0,
  } : {}),
  ...(details.native_media_asset_count !== undefined ? {
    native_media_asset_count: Number(details.native_media_asset_count) || 0,
  } : {}),
  ...(details.native_media_bytes !== undefined ? {
    native_media_bytes: Number(details.native_media_bytes) || 0,
  } : {}),
  ...(details.native_media_missing_count !== undefined ? {
    native_media_missing_count: Number(details.native_media_missing_count) || 0,
  } : {}),
});

const dismissFirstTimeGuideForContent = (reason, details = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  let dismissed = false;

  try {
    if (window.firstTimeGuide && typeof window.firstTimeGuide.destroy === 'function') {
      window.firstTimeGuide.destroy();
      dismissed = true;
    }
  } catch (error) {
    logger.warn('Failed to destroy first-time guide instance', { error: error?.message, reason }, 'TVTestPlayer');
  }

  try {
    const guideContainer = document.getElementById('first-time-guide-container');
    if (guideContainer) {
      guideContainer.remove();
      dismissed = true;
    }
  } catch (error) {
    logger.warn('Failed to remove first-time guide container', { error: error?.message, reason }, 'TVTestPlayer');
  }

  if (dismissed || window.firstTimeGuide) {
    window.firstTimeGuide = null;
  }

  if (dismissed) {
    logger.info('Dismissed first-time guide after content became available', { reason, ...details }, 'TVTestPlayer');
  }
};

const shouldDismissFirstTimeGuideForContent = (content) => {
  const hasStreams = content?.all_default_streams?.length > 0;
  const hasAds = content?.overlay_advertisements?.length > 0;
  const hasLBar = content?.l_bar_content?.enabled && ['l-bar', 'lbar'].includes(String(content?.display_preferences?.display_mode || '').trim().toLowerCase());
  return hasStreams || hasAds || hasLBar || hasLoopComposerPlayback(content);
};

const resolveTrillboardsApiBase = () => {
  const fallback = 'https://api.trillboards.com';
  try {
    const configured = window.__TRILLBOARDS_API_BASE;
    if (typeof configured === 'string' && configured.trim()) {
      return configured.replace(/\/+$/, '');
    }
    const host = window.location?.hostname || '';
    if (host === 'india-loop.qa-screen.trillboards.com' || host.includes('india-loop.qa-screen.trillboards.com')) {
      return 'https://india-loop.qa-api.trillboards.com';
    }
    if (host.includes('qa-screen.trillboards.com') || host.startsWith('qa-')) {
      return 'https://qa-api.trillboards.com';
    }
  } catch (_) {}
  return fallback;
};

const fetchWithTimeout = (url, options = {}, timeoutMs = CONTENT_BOOTSTRAP_TIMEOUT_MS) => {
  if (typeof fetch !== 'function') {
    return Promise.reject(new Error('fetch_unavailable'));
  }

  if (typeof AbortController === 'undefined') {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(`fetch_timeout_${timeoutMs}ms`)), timeoutMs);
    });

    return Promise.race([fetch(url, options), timeoutPromise]).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, {
    ...options,
    signal: controller.signal
  }).finally(() => {
    clearTimeout(timeoutId);
  });
};

const getResponseETag = (response) => {
  if (!response?.headers || typeof response.headers.get !== 'function') {
    return null;
  }
  return response.headers.get('etag');
};

const isHardScreenNotFoundResponse = (response, data) => {
  if (response?.status === 404) return true;
  if (Number(data?.code) === 404) return true;
  return typeof data?.message === 'string' && /screen not found/i.test(data.message);
};

const buildBootstrapBrowserSignalPayload = (state, details, nowMs) => {
  const win = typeof window !== 'undefined' ? window : {};
  const screenInfo = win.screen || {};
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const width = Number(screenInfo.width || win.innerWidth || 0);
  const height = Number(screenInfo.height || win.innerHeight || 0);
  const orientationType = screenInfo.orientation?.type
    || (width > height ? 'landscape' : (height > width ? 'portrait' : 'unknown'));
  const cacheStatus = normalizeCacheStatusDetails(details);

  return {
    collected_at: new Date(nowMs).toISOString(),
    user_agent: nav.userAgent || null,
    cache_status: cacheStatus,
    screen: {
      width,
      height,
      avail_width: Number(screenInfo.availWidth || 0),
      avail_height: Number(screenInfo.availHeight || 0),
      pixel_ratio: Number(win.devicePixelRatio || 1),
      orientation: orientationType,
    },
    touch: {
      max_touch_points: nav.maxTouchPoints ?? null,
      hover_support: typeof win.matchMedia === 'function'
        ? win.matchMedia('(hover: hover)').matches
        : null,
    },
    hardware: {
      device_memory_gb: nav.deviceMemory ?? null,
      cpu_cores: nav.hardwareConcurrency ?? null,
    },
    codecs: {
      media_source_supported: typeof win.MediaSource !== 'undefined',
    },
    platform: {
      language: nav.language || null,
      platform: nav.platform || null,
    },
    bootstrap: {
      state,
      details: details || {},
      cache_status: cacheStatus,
      updated_at_ms: nowMs,
      overlay_container_present: Boolean(document.getElementById('trillboards-overlay-container')),
      loading_text_present: Boolean(document.body?.textContent?.includes('Loading content...')),
    },
  };
};

const normalizeMediaType = (value) => {
  const clean = String(value || '').trim().toLowerCase();
  if (!clean) return null;
  if (clean === 'video' || clean.startsWith('video/') || ['mp4', 'webm', 'mov', 'm4v'].includes(clean)) {
    return 'video';
  }
  if (clean === 'image' || clean.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'].includes(clean)) {
    return 'image';
  }
  return null;
};

const inferMediaTypeFromUrl = (...urls) => {
  for (const url of urls) {
    const clean = String(url || '').split('?')[0].toLowerCase();
    if (/\.(mp4|webm|mov|m4v)$/.test(clean)) return 'video';
    if (/\.(png|jpe?g|webp|gif|avif)$/.test(clean)) return 'image';
  }
  return 'media';
};

const resolveSelectedCreativeMedia = (loopPlaybackState) => {
  // The v2 overlay is the single visual owner for server-resolved creatives and
  // IMA. The React shell supplies only the neutral surface and slot timing.
  if (loopPlaybackState?.loop_manifest_source === 'resolved_loop_plan_v2') {
    return null;
  }
  const url = loopPlaybackState?.selected_creative_asset_url;
  const source = String(loopPlaybackState?.selected_creative_source || '').toLowerCase();
  if (!url || source === 'default_stream') return null;
  const originalUrl = loopPlaybackState?.selected_creative_original_asset_url;
  return {
    type: normalizeMediaType(loopPlaybackState?.selected_creative_media_type)
      || inferMediaTypeFromUrl(url, originalUrl),
    url,
    originalUrl,
    name: loopPlaybackState?.selected_creative_id || 'Selected creative',
  };
};

const resolveLoopShellGate = (loopPlaybackState) => {
  if (!loopPlaybackState) {
    return { allowDefaultStreams: true, reason: 'interval_screen' };
  }

  if (loopPlaybackState.strict_loop_surface === true) {
    // The v2 canary overlay remains the sole owner of resolved creatives and
    // IMA, but its background fallback must stay mounted underneath. Keeping
    // the iframe/video alive prevents an unresolved booked slot or an IMA
    // no-fill from turning the whole screen black, and avoids a slow remount at
    // the slot boundary. The overlay's neutral handoff surface still covers
    // the background until a resolved creative or eligible IMA fill is ready.
    if (loopPlaybackState.loop_manifest_source === 'resolved_loop_plan_v2') {
      return { allowDefaultStreams: true, reason: 'loop_canary_background_fallback' };
    }
    return { allowDefaultStreams: false, reason: 'loop_canary_strict_surface' };
  }

  const tier = String(loopPlaybackState.tier || '').toLowerCase();
  if (tier === 'locked') {
    return { allowDefaultStreams: false, reason: 'loop_gate_non_house' };
  }
  if (tier === 'house') {
    return { allowDefaultStreams: true, reason: 'house_slot' };
  }
  if (tier === 'residual') {
    return { allowDefaultStreams: true, reason: 'residual_fallback_content' };
  }
  if (tier === 'content') {
    return { allowDefaultStreams: true, reason: 'content_slot' };
  }

  return { allowDefaultStreams: false, reason: 'loop_gate_unknown_tier' };
};

const resolveRenderedLoopSource = (loopPlaybackState, currentVideoId, shellMedia, selectedCreativeMedia = null) => {
  const shellGate = resolveLoopShellGate(loopPlaybackState);
  if (loopPlaybackState && selectedCreativeMedia?.url) {
    const selectionSource = loopPlaybackState?.selected_creative_selection_source === 'resolved_creative'
      ? 'resolved_creative'
      : 'creative_rotation';
    return {
      type: selectedCreativeMedia.type || 'media',
      source: selectionSource,
      id: loopPlaybackState?.selected_creative_id || selectedCreativeMedia.name || null,
      url: selectedCreativeMedia.url,
      media_present: true,
      reason: selectionSource === 'resolved_creative' ? 'loop_resolved_creative' : 'loop_creative_rotation',
    };
  }

  if (!shellGate.allowDefaultStreams) {
    return {
      type: 'none',
      source: 'none',
      id: null,
      url: null,
      media_present: false,
      reason: shellGate.reason,
    };
  }

  if (currentVideoId) {
    return {
      type: 'youtube',
      source: 'default_stream',
      id: currentVideoId,
      url: `youtube:${currentVideoId}`,
      media_present: true,
      reason: loopPlaybackState ? shellGate.reason : 'interval_playback',
    };
  }

  if (shellMedia?.url) {
    return {
      type: shellMedia.type || 'media',
      source: 'default_stream',
      id: shellMedia.name || null,
      url: shellMedia.url,
      media_present: true,
      reason: loopPlaybackState ? shellGate.reason : 'interval_playback',
    };
  }

  return {
    type: 'none',
    source: 'none',
    id: null,
    url: null,
    media_present: false,
    reason: loopPlaybackState ? `empty_${shellGate.reason}` : 'no_rendered_media',
  };
};

const kickKioskVideoPlayback = (video, reason = 'video_ready') => {
  if (!video || video.isConnected === false) return;

  try {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (!video.paused && !video.ended) return;
    if (typeof video.play !== 'function') return;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error) => {
        logger.warn('Kiosk video play nudge failed', {
          reason,
          error: error?.message || String(error),
        }, 'TVTestPlayer');
      });
    }
  } catch (error) {
    logger.warn('Kiosk video play nudge threw', {
      reason,
      error: error?.message || String(error),
    }, 'TVTestPlayer');
  }
};

const recoverKioskVideoPlayback = (video, media, loopPlaybackState) => {
  if (!video || video.isConnected === false) return;

  const currentSrc = video.currentSrc || video.src || '';
  const originalUrl = media?.originalUrl || loopPlaybackState?.selected_creative_original_asset_url || null;
  const shouldRetryOriginal = Boolean(
    originalUrl
    && currentSrc
    && currentSrc !== originalUrl
    && currentSrc.startsWith('blob:')
    && video.dataset?.originalAssetRetry !== 'true'
  );

  if (!shouldRetryOriginal) {
    logger.error('Kiosk video failed to render', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      src: currentSrc,
      errorCode: video.error?.code ?? null,
      error: video.error?.message || null,
    }, 'TVTestPlayer');
    return;
  }

  try {
    logger.warn('Kiosk video blob failed; retrying original asset URL', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      blobUrl: currentSrc,
      originalUrl,
      errorCode: video.error?.code ?? null,
      error: video.error?.message || null,
    }, 'TVTestPlayer');
    video.dataset.originalAssetRetry = 'true';
    video.src = originalUrl;
    video.load();
    kickKioskVideoPlayback(video, 'loop_video_error_original_retry');
  } catch (error) {
    logger.warn('Kiosk video original asset retry failed', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      originalUrl,
      error: error?.message || String(error),
    }, 'TVTestPlayer');
  }
};

const recoverKioskImagePlayback = (image, media, loopPlaybackState) => {
  if (!image || image.isConnected === false) return;

  const currentSrc = image.currentSrc || image.src || '';
  const originalUrl = media?.originalUrl || loopPlaybackState?.selected_creative_original_asset_url || null;
  const shouldRetryOriginal = Boolean(
    originalUrl
    && currentSrc
    && currentSrc !== originalUrl
    && currentSrc.startsWith('blob:')
    && image.dataset?.originalAssetRetry !== 'true'
  );

  if (!shouldRetryOriginal) {
    logger.error('Kiosk image failed to render', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      src: currentSrc,
    }, 'TVTestPlayer');
    return;
  }

  try {
    logger.warn('Kiosk image blob failed; retrying original asset URL', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      blobUrl: currentSrc,
      originalUrl,
    }, 'TVTestPlayer');
    image.dataset.originalAssetRetry = 'true';
    image.src = originalUrl;
  } catch (error) {
    logger.warn('Kiosk image original asset retry failed', {
      creativeId: loopPlaybackState?.selected_creative_id || media?.name || null,
      slotIndex: loopPlaybackState?.slot_index ?? null,
      originalUrl,
      error: error?.message || String(error),
    }, 'TVTestPlayer');
  }
};

const KIOSK_VIDEO_EVIDENCE_LIMIT = 100;

const recordKioskVideoLifecycle = (eventName, video, media, loopPlaybackState) => {
  if (!video || typeof window === 'undefined' || typeof document === 'undefined') return;

  let computedBodyTransform = null;
  try {
    computedBodyTransform = window.getComputedStyle(document.body).transform || null;
  } catch (_) {}

  const evidence = {
    event: eventName,
    collected_at_ms: Date.now(),
    instance_id: video.dataset?.kioskVideoInstance || null,
    src: video.getAttribute('src') || media?.url || video.currentSrc || null,
    current_src: video.currentSrc || null,
    plan_id: loopPlaybackState?.plan_id ?? null,
    cycle_number: loopPlaybackState?.cycle_number ?? null,
    slot_index: loopPlaybackState?.slot_index ?? null,
    body_class_list: Array.from(document.body.classList || []),
    rotation_css_var: document.documentElement.style.getPropertyValue('--trillboards-rotation') || null,
    computed_body_transform: computedBodyTransform,
    video_width: Number(video.videoWidth) || 0,
    video_height: Number(video.videoHeight) || 0,
    hardware_surface_bypass: null,
  };

  try {
    const history = Array.isArray(window.__TRILLBOARDS_KIOSK_VIDEO_EVIDENCE__)
      ? window.__TRILLBOARDS_KIOSK_VIDEO_EVIDENCE__
      : [];
    window.__TRILLBOARDS_KIOSK_VIDEO_EVIDENCE__ = [...history, evidence].slice(-KIOSK_VIDEO_EVIDENCE_LIMIT);
    window.dispatchEvent(new CustomEvent('trillboards:kiosk-video-lifecycle', { detail: evidence }));
  } catch (_) {}

  logger.debug('Kiosk video lifecycle', evidence, 'TVTestPlayer');
};

let kioskVideoInstanceSequence = 0;

const PersistentKioskVideo = ({ active, media, loopPlaybackState, managedByOverlay = true }) => {
  const videoRef = useRef(null);
  const [overlayOwnsPlayback, setOverlayOwnsPlayback] = useState(false);
  const [nativeOwnsPlayback, setNativeOwnsPlayback] = useState(() => {
    const bridge = window.TrillboardsNativeUploadedVideo;
    try {
      return Boolean(bridge && typeof bridge.isActive === 'function' && bridge.isActive() === true);
    } catch (_) {
      return false;
    }
  });
  // Native composition can end while the overlay still owns the queue (photos
  // or HTML5 fallback). Do not resurrect the bootstrap video's stale source.
  const [nativeQueueOwnsPlayback, setNativeQueueOwnsPlayback] = useState(nativeOwnsPlayback);
  const suppressShellVideo = nativeOwnsPlayback || (managedByOverlay && nativeQueueOwnsPlayback);
  const lastMediaRef = useRef(null);
  const latestStateRef = useRef(loopPlaybackState);
  const lastPlaybackIdentityRef = useRef(null);
  const lastActiveUrlRef = useRef(null);
  const instanceIdRef = useRef(null);
  if (!instanceIdRef.current) {
    kioskVideoInstanceSequence += 1;
    instanceIdRef.current = `kiosk-video-${kioskVideoInstanceSequence}`;
  }

  const isPlaybackActive = Boolean(active && media?.url);
  const isShellPlaybackActive = isPlaybackActive && !overlayOwnsPlayback && !suppressShellVideo;
  if (isPlaybackActive) {
    lastMediaRef.current = media;
  }
  latestStateRef.current = loopPlaybackState;
  const attachedMedia = lastMediaRef.current;
  const hasAttachedMedia = Boolean(attachedMedia?.url);
  const playbackIdentity = isPlaybackActive
    ? [
      media.url,
      loopPlaybackState?.plan_id ?? 'interval',
      loopPlaybackState?.cycle_number ?? 'na',
      loopPlaybackState?.slot_index ?? 'na',
    ].join('|')
    : null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    recordKioskVideoLifecycle('mount', video, lastMediaRef.current, latestStateRef.current);
    return () => {
      recordKioskVideoLifecycle('unmount', video, lastMediaRef.current, latestStateRef.current);
    };
  }, [hasAttachedMedia]);

  useEffect(() => {
    if (!isBrightSignRuntime()) return undefined;

    const isOverlayReadyEvidence = (evidence) => {
      const instanceId = String(evidence?.instance_id || '');
      const eventName = String(evidence?.event || '');
      return instanceId.startsWith('overlay-video-')
        && ['can_play', 'persistent_source_ready'].includes(eventName);
    };
    const existingEvidence = Array.isArray(window.__TRILLBOARDS_KIOSK_VIDEO_EVIDENCE__)
      ? window.__TRILLBOARDS_KIOSK_VIDEO_EVIDENCE__
      : [];
    if (existingEvidence.some(isOverlayReadyEvidence)) {
      setOverlayOwnsPlayback(true);
    }

    const handleOverlayReady = (event) => {
      if (isOverlayReadyEvidence(event?.detail)) {
        setOverlayOwnsPlayback(true);
      }
    };
    window.addEventListener('trillboards:kiosk-video-lifecycle', handleOverlayReady);
    return () => window.removeEventListener('trillboards:kiosk-video-lifecycle', handleOverlayReady);
  }, []);

  useEffect(() => {
    const handleNativeOwnership = (event) => {
      const nativeActive = event?.detail?.active === true;
      if (nativeActive) {
        setNativeQueueOwnsPlayback(true);
        const video = videoRef.current;
        if (video) {
          try {
            if (typeof video.pause === 'function') video.pause();
            video.removeAttribute('src');
            if (typeof video.load === 'function') video.load();
          } catch (_) {}
        }
        // Force a fresh shell playback handshake if Media3 later falls back.
        lastPlaybackIdentityRef.current = null;
        lastActiveUrlRef.current = null;
      }
      setNativeOwnsPlayback(nativeActive);
    };
    window.addEventListener('trillboards:native-uploaded-video-active', handleNativeOwnership);
    return () => window.removeEventListener('trillboards:native-uploaded-video-active', handleNativeOwnership);
  }, []);

  useEffect(() => {
    const syncCompositorOpacity = () => {
      const video = videoRef.current;
      if (!video) return;
      video.style.opacity = getVideoOpacity();
    };

    syncCompositorOpacity();
    if (typeof MutationObserver === 'undefined' || !document.body) return undefined;

    // ScreenConfig is populated after the React shell can render. Observe the
    // canonical rotation class so an already-mounted kiosk video cannot remain
    // at opacity: 1 and escape into Amazon WebView's unrotated SurfaceView.
    const rotationObserver = new MutationObserver(syncCompositorOpacity);
    rotationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => rotationObserver.disconnect();
  }, [hasAttachedMedia]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isShellPlaybackActive || !playbackIdentity) {
      try {
        if (!video.paused && typeof video.pause === 'function') {
          video.pause();
        }
      } catch (_) {}
      if (nativeOwnsPlayback) {
        try {
          video.removeAttribute('src');
          if (typeof video.load === 'function') video.load();
        } catch (_) {}
      }
      recordKioskVideoLifecycle('playback_hidden', video, attachedMedia, loopPlaybackState);
      return;
    }
    if (lastPlaybackIdentityRef.current === playbackIdentity) return;

    const previousUrl = lastActiveUrlRef.current;
    lastPlaybackIdentityRef.current = playbackIdentity;
    lastActiveUrlRef.current = media.url;
    if (video.dataset) {
      delete video.dataset.originalAssetRetry;
    }
    if (previousUrl && previousUrl === media.url) {
      try {
        video.currentTime = 0;
      } catch (_) {}
    }
    recordKioskVideoLifecycle(previousUrl ? 'source_or_slot_change' : 'source_attached', video, media, loopPlaybackState);
    kickKioskVideoPlayback(video, 'loop_video_source_change');
  }, [attachedMedia, isShellPlaybackActive, media, nativeOwnsPlayback, loopPlaybackState, playbackIdentity]);

  if (!hasAttachedMedia || suppressShellVideo) {
    return null;
  }

  return (
    <video
      className="trillboards-kiosk-video"
      src={nativeOwnsPlayback ? undefined : attachedMedia?.url}
      ref={videoRef}
      data-kiosk-video-instance={instanceIdRef.current}
      data-playback-active={isShellPlaybackActive ? 'true' : 'false'}
      data-overlay-owns-playback={overlayOwnsPlayback ? 'true' : 'false'}
      aria-hidden={isShellPlaybackActive ? 'false' : 'true'}
      autoPlay
      muted
      controls={false}
      controlsList="nodownload nofullscreen noremoteplayback"
      disablePictureInPicture
      disableRemotePlayback
      playsInline
      webkit-playsinline=""
      x-webkit-airplay="deny"
      poster={TRANSPARENT_VIDEO_POSTER}
      preload="auto"
      loop
      onLoadedMetadata={(event) => {
        recordKioskVideoLifecycle('loaded_metadata', event.currentTarget, attachedMedia, latestStateRef.current);
        kickKioskVideoPlayback(event.currentTarget, 'loop_video_loaded_metadata');
      }}
      onCanPlay={(event) => {
        recordKioskVideoLifecycle('can_play', event.currentTarget, attachedMedia, latestStateRef.current);
        kickKioskVideoPlayback(event.currentTarget, 'loop_video_can_play');
      }}
      onError={(event) => recoverKioskVideoPlayback(
        event.currentTarget,
        isPlaybackActive ? media : attachedMedia,
        latestStateRef.current
      )}
      onPause={(event) => {
        const video = event.currentTarget;
        if (!isShellPlaybackActive || !video || video.ended || video.error) return;
        setTimeout(() => kickKioskVideoPlayback(video, 'loop_video_pause_retry'), 150);
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: getKioskContentObjectFit(),
        objectPosition: 'center',
        opacity: getVideoOpacity(),
        visibility: isShellPlaybackActive ? 'visible' : 'hidden',
        pointerEvents: 'none',
        zIndex: 1
      }}
    />
  );
};

const buildLoopPlaybackBrowserSignalPayload = (loopPlaybackState, renderedSource, nowMs, sessionId = null, cacheStatusDetails = {}) => {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const shellGate = resolveLoopShellGate(loopPlaybackState);

  return {
    collected_at: new Date(nowMs).toISOString(),
    user_agent: nav.userAgent || null,
    cache_status: normalizeCacheStatusDetails(cacheStatusDetails),
    loop_playback: {
      session_id: sessionId || null,
      plan_id: loopPlaybackState?.plan_id ?? null,
      cycle_number: loopPlaybackState?.cycle_number ?? null,
      slot_index: loopPlaybackState?.slot_index ?? null,
      slot_offset_ms: loopPlaybackState?.slot_offset_ms ?? null,
      slot_duration_ms: loopPlaybackState?.slot_duration_ms ?? null,
      tier: loopPlaybackState?.tier || null,
      creative_class: loopPlaybackState?.creative_class || null,
      fill_source: loopPlaybackState?.fill_source || null,
      reservation_id: loopPlaybackState?.reservation_id || null,
      selected_creative_id: loopPlaybackState?.selected_creative_id || null,
      selected_creative_source: loopPlaybackState?.selected_creative_source || null,
      selected_creative_asset_url: loopPlaybackState?.selected_creative_asset_url || null,
      selected_creative_original_asset_url: loopPlaybackState?.selected_creative_original_asset_url || null,
      selected_creative_media_type: loopPlaybackState?.selected_creative_media_type || null,
      selected_creative_selection_source: loopPlaybackState?.selected_creative_selection_source || null,
      creative_rotation_policy: loopPlaybackState?.creative_rotation_policy || null,
      creative_rotation_candidate_count: loopPlaybackState?.creative_rotation_candidate_count ?? 0,
      creative_rotation_eligible_count: loopPlaybackState?.creative_rotation_eligible_count ?? 0,
      creative_rotation_order: loopPlaybackState?.creative_rotation_order ?? null,
      creative_rotation_used_default_fallback: loopPlaybackState?.creative_rotation_used_default_fallback === true,
      elapsed_ms: loopPlaybackState?.elapsed_ms ?? null,
      remaining_ms: loopPlaybackState?.remaining_ms ?? null,
      loop_duration_ms: loopPlaybackState?.loop_duration_ms ?? null,
      transition_timestamp_ms: nowMs,
      rendered_source: renderedSource,
      stream_gate: {
        allow_default_streams: shellGate.allowDefaultStreams,
        reason: shellGate.reason,
      },
    },
  };
};

const sendLoopPlaybackBrowserSignal = (screenId, loopPlaybackState, renderedSource, sentRef, sessionId = null, cacheStatusDetails = {}, options = {}) => {
  if (!screenId || screenId === 'null' || screenId === 'undefined' || !loopPlaybackState) {
    return;
  }

  try {
    const nowMs = Date.now();
    const transitionKey = [
      loopPlaybackState.plan_id,
      loopPlaybackState.cycle_number,
      loopPlaybackState.slot_index,
      loopPlaybackState.tier,
      loopPlaybackState.reservation_id || '',
      loopPlaybackState.selected_creative_id || '',
      renderedSource?.source,
      renderedSource?.id || renderedSource?.url || renderedSource?.type,
    ].join(':');

    if (sentRef.current?.transitionKey === transitionKey) {
      return;
    }
    sentRef.current = { transitionKey, sentAtMs: nowMs };

    const url = `${resolveTrillboardsApiBase()}/v2/earner/browser-signals/${screenId}`;
    const nativeStatus = readNativeLoopOfflineStatus(screenId) || {};
    const payload = buildLoopPlaybackBrowserSignalPayload(loopPlaybackState, renderedSource, nowMs, sessionId, {
      ...cacheStatusDetails,
      ...nativeStatus,
      queued_event_count: getQueuedTelemetryCount(screenId),
    });
    putNativePlaybackState(screenId, options.fingerprint, loopPlaybackState, renderedSource, {
      sessionId,
      transitionTimestampMs: nowMs,
    });
    return sendBrowserSignalWithOfflineQueue(screenId, url, payload, { fingerprint: options.fingerprint });
  } catch (_) {}
};

const sendBootstrapBrowserSignal = (screenId, state, details, throttleRef, options = {}) => {
  if (!screenId || screenId === 'null' || screenId === 'undefined') {
    return;
  }

  try {
    const nowMs = Date.now();
    const reason = typeof details?.reason === 'string' ? details.reason : '';
    const throttleKey = `${state}:${reason}`;
    const throttleMap = throttleRef.current || {};
    if (throttleMap[throttleKey] && nowMs - throttleMap[throttleKey] < BOOTSTRAP_SIGNAL_THROTTLE_MS) {
      return;
    }
    throttleMap[throttleKey] = nowMs;
    throttleRef.current = throttleMap;

    const url = `${resolveTrillboardsApiBase()}/v2/earner/browser-signals/${screenId}`;
    const nativeStatus = readNativeLoopOfflineStatus(screenId) || {};
    const payload = buildBootstrapBrowserSignalPayload(state, {
      ...(details || {}),
      ...nativeStatus,
      queued_event_count: getQueuedTelemetryCount(screenId),
    }, nowMs);
    return sendBrowserSignalWithOfflineQueue(screenId, url, payload, options);
  } catch (_) {}
};

const YOUTUBE_BOOT_PLAY_ATTEMPTS_MS = [0, 250, 800, 1500, 3000];

const postYouTubeBootCommand = (iframe, func, args = []) => {
  postYouTubePlayerCommand(iframe, func, args);
};

const postTrillboardsScreenVisible = (state, details = {}) => {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }

  try {
    window.parent.postMessage({
      type: 'trillboards:screen-visible',
      state,
      ...details,
    }, '*');
  } catch (_) {}
};

const postTrillboardsScreenHeartbeat = (state, details = {}) => {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }

  try {
    window.parent.postMessage({
      type: 'trillboards:screen-heartbeat',
      state,
      at: Date.now(),
      ...details,
    }, '*');
  } catch (_) {}
};

const nudgeYouTubeBootPlayback = (reason = 'boot') => {
  try {
    YOUTUBE_BOOT_PLAY_ATTEMPTS_MS.forEach((delayMs) => {
      setTimeout(() => {
        const iframe = document.getElementById('yt-player');
        if (!iframe || iframe.style.display === 'none' || !iframe.contentWindow) return;
        postYouTubeListening(iframe, 'trillboards-boot');
        postYouTubeBootCommand(iframe, 'mute');
        postYouTubeBootCommand(iframe, 'addEventListener', ['onStateChange']);
        postYouTubeBootCommand(iframe, 'playVideo');
      }, delayMs);
    });
    logger.debug('Nudged YouTube boot playback', { reason }, 'TVTestPlayer');
  } catch (_) {}
};

const shellBackgroundStyle = {
  position: 'fixed',
  inset: 0,
  width: '100%',
  height: '100%',
  background:
    'radial-gradient(circle at 18% 20%, rgba(108, 216, 255, 0.24), transparent 32%), radial-gradient(circle at 84% 18%, rgba(255, 194, 71, 0.18), transparent 30%), linear-gradient(135deg, #172044 0%, #102b4e 48%, #16213e 100%)',
  color: '#ffffff',
  overflow: 'hidden',
  margin: 0,
  padding: 0
};

const normalizeTrillboardsOverlayContainer = () => {
  if (typeof document === 'undefined') return null;
  const playerRoot = document.getElementById('tv-player-root');
  if (!playerRoot) return null;
  const nodes = Array.from(document.querySelectorAll('#trillboards-overlay-container'));
  if (!nodes.length) return null;
  const preferred = nodes.find((node) => playerRoot.contains(node)) || nodes[0];
  if (!playerRoot.contains(preferred)) {
    playerRoot.appendChild(preferred);
  }
  try {
    const rootStyle = typeof getComputedStyle === 'function' ? getComputedStyle(playerRoot) : null;
    if (rootStyle?.position === 'static') {
      playerRoot.style.position = 'relative';
    }
  } catch (_) {}
  Object.assign(preferred.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '1000',
    overflow: 'hidden'
  });
  nodes.forEach((node) => {
    if (node === preferred) return;
    try {
      while (node.firstChild) {
        preferred.appendChild(node.firstChild);
      }
      node.remove();
    } catch (_) {}
  });
  return preferred;
};

const renderBrandedStatus = ({ screenId, title, subtitle }) => (
  <div style={{
    ...shellBackgroundStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontFamily: 'Arial, Helvetica, sans-serif'
  }}>
    <div style={{
      width: 'min(1120px, 84vw)',
      padding: '44px 56px',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: '32px',
      background: 'rgba(7,18,42,0.66)',
      boxShadow: '0 24px 72px rgba(0,0,0,0.28)'
    }}>
      <div style={{
        width: '96px',
        height: '96px',
        borderRadius: '24px',
        margin: '0 auto 28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5c84b',
        color: '#07122a',
        fontSize: '58px',
        fontWeight: 800,
        lineHeight: 1
      }}>T</div>
      <div style={{
        marginBottom: '16px',
        fontSize: '54px',
        lineHeight: 1.08,
        fontWeight: 800
      }}>{title}</div>
      <div style={{
        margin: '0 auto 28px',
        maxWidth: '880px',
        fontSize: '30px',
        lineHeight: 1.35,
        color: 'rgba(255,255,255,0.82)'
      }}>{subtitle}</div>
      <div style={{
        fontSize: '20px',
        color: 'rgba(255,255,255,0.62)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      }}>Screen ID {String(screenId || 'connecting').slice(-8).toUpperCase()}</div>
    </div>
  </div>
);

const renderContentShell = ({
  currentVideoId,
  currentStartSeconds,
  shellMedia,
  loopPlaybackState,
  showWaitingForContent = false,
}) => {
  const shellGate = resolveLoopShellGate(loopPlaybackState);
  const isLoopShellGated = !shellGate.allowDefaultStreams;
  const selectedCreativeMedia = resolveSelectedCreativeMedia(loopPlaybackState);
  const hasSelectedCreativeMedia = Boolean(selectedCreativeMedia?.url);
  const renderedVideoId = hasSelectedCreativeMedia || isLoopShellGated ? null : currentVideoId;
  const renderedShellMedia = hasSelectedCreativeMedia ? selectedCreativeMedia : (isLoopShellGated ? null : shellMedia);
  const renderedSource = resolveRenderedLoopSource(loopPlaybackState, currentVideoId, shellMedia, selectedCreativeMedia);
  const embedUrl = renderedVideoId
    ? buildYouTubeEmbedUrl(renderedVideoId, { startSeconds: currentStartSeconds })
    : null;

  return (
    <div
      id="tv-player-root"
      data-loop-plan-id={loopPlaybackState?.plan_id || ''}
      data-loop-slot-index={loopPlaybackState?.slot_index ?? ''}
      data-loop-tier={loopPlaybackState?.tier || ''}
      data-rendered-source={renderedSource.source}
      data-rendered-source-type={renderedSource.type}
      data-rendered-reason={renderedSource.reason || ''}
      data-rendered-media-present={renderedSource.media_present ? 'true' : 'false'}
      style={{
      ...shellBackgroundStyle,
      overflow: 'hidden',
      margin: 0,
      padding: 0
    }}>
      <div
        id="trillboards-stream-content"
        data-trillboards-react-content-viewport="true"
        data-trillboards-fullscreen-content={hasSelectedCreativeMedia ? 'true' : 'false'}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: hasSelectedCreativeMedia ? '100%' : 'var(--trillboards-content-width, 100%)',
          height: hasSelectedCreativeMedia ? '100%' : 'var(--trillboards-content-height, 100%)',
          overflow: 'hidden',
          background: 'black',
          pointerEvents: 'none',
          zIndex: 1
        }}>
        {showWaitingForContent ? (
          <div
            id="trillboards-react-waiting-for-content"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center',
              fontFamily: 'Arial, Helvetica, sans-serif',
              color: '#ffffff',
              zIndex: 1,
            }}
          >
            <div style={{
              width: 'min(1120px, 84vw)',
              padding: '44px 56px',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: '32px',
              background: 'rgba(7,18,42,0.66)',
              boxShadow: '0 24px 72px rgba(0,0,0,0.28)',
            }}>
              <div style={{
                width: '96px',
                height: '96px',
                borderRadius: '24px',
                margin: '0 auto 28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#01bea5',
                color: '#07122a',
                fontSize: '58px',
                fontWeight: 800,
                lineHeight: 1,
              }}>T</div>
              <div style={{
                marginBottom: '16px',
                fontSize: '54px',
                lineHeight: 1.08,
                fontWeight: 800,
              }}>Screen Paired</div>
              <div style={{
                margin: '0 auto',
                maxWidth: '880px',
                fontSize: '30px',
                lineHeight: 1.35,
                color: 'rgba(255,255,255,0.82)',
              }}>
                Waiting for content to be added from the Trillboards portal.
              </div>
            </div>
          </div>
        ) : null}
        {renderedVideoId ? (
          <iframe
            id="yt-player"
            src={embedUrl}
            allow={YOUTUBE_IFRAME_ALLOW}
            allowFullScreen
            referrerPolicy={YOUTUBE_REFERRER_POLICY}
            title="YouTube Player"
            key={`${renderedVideoId}_${Math.floor(currentStartSeconds || 0)}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'transparent'
            }}
            onLoad={() => {
              logger.info('YouTube iframe loaded for video', { videoId: renderedVideoId }, 'TVTestPlayer');
              nudgeYouTubeBootPlayback('iframe_load');
            }}
            onError={(e) => {
              logger.error('YouTube iframe error', { error: e?.message }, 'TVTestPlayer');
            }}
          />
        ) : null}

        {!renderedVideoId && renderedShellMedia?.type === 'image' ? (
          <img
            src={renderedShellMedia.url}
            key={`${renderedShellMedia.url}_${loopPlaybackState?.plan_id || 'interval'}_${loopPlaybackState?.slot_index ?? 'na'}_${loopPlaybackState?.cycle_number ?? 'na'}`}
            alt={renderedShellMedia.name || 'Screen content'}
            onError={(event) => recoverKioskImagePlayback(event.currentTarget, renderedShellMedia, loopPlaybackState)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: getKioskContentObjectFit(),
              objectPosition: 'center',
              zIndex: 1
            }}
          />
        ) : null}

        <PersistentKioskVideo
          active={!renderedVideoId && renderedShellMedia?.type === 'video'}
          media={renderedShellMedia?.type === 'video' ? renderedShellMedia : null}
          loopPlaybackState={loopPlaybackState}
          managedByOverlay={!hasSelectedCreativeMedia}
        />

        {!renderedVideoId && renderedShellMedia?.type === 'media' ? (
          <img
            src={renderedShellMedia.url}
            key={`${renderedShellMedia.url}_${loopPlaybackState?.plan_id || 'interval'}_${loopPlaybackState?.slot_index ?? 'na'}_${loopPlaybackState?.cycle_number ?? 'na'}`}
            alt={renderedShellMedia.name || 'Selected creative'}
            onError={(event) => recoverKioskImagePlayback(event.currentTarget, renderedShellMedia, loopPlaybackState)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: getKioskContentObjectFit(),
              objectPosition: 'center',
              zIndex: 1
            }}
          />
        ) : null}
      </div>

      <div
        id="trillboards-overlay-container"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1000,
          overflow: 'hidden'
        }}
      />
    </div>
  );
};

const TVTestPlayer = ({ myFingerPrint, myId, socket, prefetchedContent }) => {
  const [currentVideoId, setCurrentVideoId] = useState(null); // Remove hardcoded Rick Astley
  const [currentStartSeconds, setCurrentStartSeconds] = useState(0); // Resume position for YouTube &start= param
  const [shellMedia, setShellMedia] = useState(null);
  const [loopPlaybackState, setLoopPlaybackState] = useState(null);
  const [loopContentVersion, setLoopContentVersion] = useState(0);
  const [overlayLoaded, setOverlayLoaded] = useState(false);
  // A paired display must never be hidden behind an unbounded content fetch.
  // Mount the waiting shell immediately and let bootstrap/polling replace it
  // when playable content arrives.
  const [isContentLoading, setIsContentLoading] = useState(false);
  const contentMonitoringRef = useRef(null); // Reference to content monitoring interval
  const cachedETagRef = useRef(null); // ETag for HTTP caching
  const healthWatchdogRef = useRef(null); // Reference to health watchdog instance
  const isMountedRef = useRef(true);
  const activeContentRef = useRef(prefetchedContent?.content || null);
  const bootstrapSignalThrottleRef = useRef({});
  const loopPlaybackSignalRef = useRef({});
  const loopMediaPrefetchRef = useRef({});
  const cacheStatusDetailsRef = useRef(normalizeCacheStatusDetails());

  const rememberActiveContent = (content) => {
    activeContentRef.current = content || null;
    setLoopContentVersion((value) => value + 1);
  };

  const applyHydratedContentToPlayer = (content, state, details = {}) => {
    if (!content) return;

    const shellPlayback = resolveShellPlayback(content, 'YYLgVq8ljfI');
    setCurrentStartSeconds(shellPlayback.startSeconds);
    setCurrentVideoId(shellPlayback.videoId);
    setShellMedia(shellPlayback.videoId ? null : resolveInitialShellMedia(content));

    window.__PREFETCHED_CONTENT__ = content;
    window.__CONTENT_PREFETCHED_AT__ = Date.now();
    rememberActiveContent(content);

    publishBootstrapState(state, details);
  };

  const publishBootstrapState = (state, details = {}) => {
    try {
      const definedDetails = Object.entries(details || {}).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
      const queuedEventCount = Number.isFinite(Number(definedDetails.queued_event_count))
        ? Number(definedDetails.queued_event_count)
        : getQueuedTelemetryCount(myId);
      const enrichedDetails = {
        ...normalizeCacheStatusDetails({
          ...definedDetails,
          queued_event_count: queuedEventCount,
        }),
        ...definedDetails,
        queued_event_count: queuedEventCount,
      };
      cacheStatusDetailsRef.current = normalizeCacheStatusDetails(enrichedDetails);
      window.__TRILLBOARD_BOOTSTRAP_STATE__ = {
        state,
        screenId: myId || null,
        updatedAtMs: Date.now(),
        details: enrichedDetails
      };
      sendBootstrapBrowserSignal(myId, state, enrichedDetails, bootstrapSignalThrottleRef, { fingerprint: myFingerPrint });
    } catch (_) {}
  };

  async function persistLoopManifest(envelope, etag, endpoint) {
    const manifest = resolveShellLoopManifest(envelope?.data);
    if (!myId || !envelope?.data || !manifest) {
      return { saved: false, reason: 'not_loop_manifest' };
    }
    // LoopManifestCache is the legacy/native offline contract. The additive v2
    // projection remains owned by the overlay and its media prefetch path.
    if (manifest.source !== 'playback_policy') {
      return { saved: false, reason: 'resolved_v2_overlay_manifest' };
    }

    const result = await saveLoopManifestCache(myId, envelope, { etag, endpoint, fingerprint: myFingerPrint });
    if (result.saved) {
      logger.info('Saved last-known-good loop manifest', {
        screenId: myId,
        indexedDbSaved: result.indexedDbSaved,
        localStorageSaved: result.localStorageSaved,
        cachedAssetCount: result.record?.cached_asset_count ?? 0,
      }, 'TVTestPlayer');
    } else {
      logger.warn('Failed to save last-known-good loop manifest', {
        screenId: myId,
        reason: result.reason,
      }, 'TVTestPlayer');
    }
    return result;
  }

  async function prefetchLoopMediaForContent(content, state, details = {}) {
    if (!myId || !content || !hasLoopComposerPlayback(content) || details.using_cached_manifest) {
      return null;
    }

    const loopPlan = resolveShellLoopManifest(content)?.loopPlan || {};
    const signature = [
      loopPlan.plan_id ?? 'no-plan',
      loopPlan.version ?? 'no-version',
      content.content_revision ?? 'no-revision',
      countLoopManifestAssets(content),
    ].join(':');

    if (loopMediaPrefetchRef.current?.signature === signature) {
      return loopMediaPrefetchRef.current.summary || null;
    }
    loopMediaPrefetchRef.current = { signature, summary: null, hydrated: false };

    const summary = await prefetchLoopMediaAssets(myId, content, { fingerprint: myFingerPrint });
    loopMediaPrefetchRef.current = { signature, summary, hydrated: false };

    logger.info('Prefetched loop media assets for offline replay', {
      screenId: myId,
      requiredAssetCount: summary.requiredAssetCount,
      cachedAssetCount: summary.cachedAssetCount,
      missingAssetCount: summary.missingAssetCount,
    }, 'TVTestPlayer');

    publishBootstrapState(state, {
      ...getContentAvailability(content),
      ...details,
      reason: `${details.reason || state}_media_prefetched`,
      using_cached_manifest: false,
      cached_asset_count: summary.cachedAssetCount,
      required_asset_count: summary.requiredAssetCount,
      missing_asset_count: summary.missingAssetCount,
    });

    try {
      const mediaHydration = await hydrateLoopMediaFromCache(myId, content, { fingerprint: myFingerPrint });
      loopMediaPrefetchRef.current = { signature, summary, hydrated: true };

      if (isMountedRef.current && mediaHydration?.cachedAssetCount > 0) {
        logger.info('Hydrated active loop media after prefetch', {
          screenId: myId,
          cachedAssetCount: mediaHydration.cachedAssetCount,
          requiredAssetCount: mediaHydration.requiredAssetCount,
          missingAssetCount: mediaHydration.missingAssetCount,
        }, 'TVTestPlayer');

        applyHydratedContentToPlayer(mediaHydration.content || content, state, {
          ...getContentAvailability(mediaHydration.content || content),
          ...details,
          reason: `${details.reason || state}_media_hydrated`,
          using_cached_manifest: false,
          cached_asset_count: mediaHydration.cachedAssetCount,
          required_asset_count: mediaHydration.requiredAssetCount,
          missing_asset_count: mediaHydration.missingAssetCount,
        });
      }
    } catch (error) {
      loopMediaPrefetchRef.current = { signature, summary, hydrated: false };
      logger.warn('Loop media hydration after prefetch failed', {
        screenId: myId,
        error: error?.message,
      }, 'TVTestPlayer');
    }

    return summary;
  }

  function applyContentToPlayer(content, state, details = {}) {
    const availability = getContentAvailability(content);
    const shouldKeepPollingLoopSlot = availability.hasLoopComposer
      && !availability.hasDisplayableContent
      && !availability.hasProgrammatic;
    const mediaCacheStatus = getLoopMediaCacheStatus(myId, content) || {};
    const expectedAssetCount = Number(countLoopManifestAssets(content)) || 0;
    const cachedAssetCount = Number.isFinite(Number(details.cached_asset_count))
      ? Number(details.cached_asset_count)
      : Number(mediaCacheStatus.cachedAssetCount) || 0;
    const requiredAssetCount = Number.isFinite(Number(details.required_asset_count))
      ? Number(details.required_asset_count)
      : Number(mediaCacheStatus.requiredAssetCount) || expectedAssetCount;
    const missingAssetCount = Number.isFinite(Number(details.missing_asset_count))
      ? Number(details.missing_asset_count)
      : Number(mediaCacheStatus.missingAssetCount) || Math.max(0, requiredAssetCount - cachedAssetCount);

    setIsContentLoading(false);
    if (!shouldKeepPollingLoopSlot) {
      stopContentMonitoring();
    }

    const shellPlayback = resolveShellPlayback(content, 'YYLgVq8ljfI');
    setCurrentStartSeconds(shellPlayback.startSeconds);
    setCurrentVideoId(shellPlayback.videoId);
    setShellMedia(shellPlayback.videoId ? null : resolveInitialShellMedia(content));

    window.__PREFETCHED_CONTENT__ = content;
    window.__CONTENT_PREFETCHED_AT__ = Date.now();
    rememberActiveContent(content);
    dismissFirstTimeGuideForContent(details.reason || state, availability);
    publishBootstrapState(state, {
      ...availability,
      cached_asset_count: cachedAssetCount,
      required_asset_count: requiredAssetCount,
      missing_asset_count: missingAssetCount,
      ...details,
    });

    if (shouldKeepPollingLoopSlot) {
      startContentMonitoring();
    }

    prefetchLoopMediaForContent(content, state, details).catch((error) => {
      logger.warn('Loop media prefetch failed', {
        screenId: myId,
        error: error?.message,
      }, 'TVTestPlayer');
    });
  }

  async function hydrateFromCachedLoopManifest(reason, extraDetails = {}) {
    const cached = await loadLoopManifestCache(myId);
    if (!cached?.hit || !cached.content) {
      return false;
    }

    if (cached.etag) {
      cachedETagRef.current = cached.etag;
    }

    const mediaHydration = await hydrateLoopMediaFromCache(myId, cached.content, { fingerprint: myFingerPrint });
    const hydratedContent = mediaHydration.content || cached.content;

    logger.warn('Using last-known-good loop manifest after content fetch failure', {
      screenId: myId,
      reason,
      source: cached.source,
      ageMs: cached.ageMs,
      cachedAssetCount: mediaHydration.cachedAssetCount,
      requiredAssetCount: mediaHydration.requiredAssetCount,
    }, 'TVTestPlayer');

    applyContentToPlayer(hydratedContent, 'content_ready_from_cache', {
      ...extraDetails,
      reason,
      using_cached_manifest: true,
      cached_manifest_age_ms: cached.ageMs,
      cached_asset_count: mediaHydration.cachedAssetCount,
      required_asset_count: mediaHydration.requiredAssetCount,
      missing_asset_count: mediaHydration.missingAssetCount,
      online_state: getNavigatorOnlineState(),
    });
    return true;
  }
  const clearStaleScreenIdentity = (reason, details = {}) => {
    logger.warn('Screen content lookup returned a hard not-found response; clearing cached screen identity', {
      screenId: myId,
      reason,
      ...details,
    }, 'TVTestPlayer');

    stopContentMonitoring();
    setCurrentVideoId(null);
    setCurrentStartSeconds(0);
    setIsContentLoading(false);
    publishBootstrapState('stale_screen_identity_cleared', { reason, ...details });

    try {
      localStorage.removeItem('screenId');
      localStorage.removeItem('trillboard_screen_identity_fingerprint');
      window.__TRILLBOARD_SCREEN_ID__ = null;
      window.myId = null;
      delete window.__PREFETCHED_CONTENT__;
      delete window.__CONTENT_PREFETCHED_AT__;
      window.dispatchEvent(new CustomEvent('trillboards:stale-screen-identity-cleared', {
        detail: { screenId: myId, reason, ...details },
      }));
    } catch (_) {}

    window.setTimeout(() => {
      try {
        window.location.reload();
      } catch (_) {}
    }, 0);
  };

  // Enforce fullscreen mode for TV kiosk experience
  const { isFullscreen } = useFullscreen({
    enabled: true,
    autoEnter: true,
    autoReenter: true,
    reenterDelayMs: 2000
  });

  // Log fullscreen state changes
  useEffect(() => {
    logger.info('Fullscreen state', { isFullscreen }, 'TVTestPlayer');
  }, [isFullscreen]);

  useEffect(() => {
    if (currentVideoId) {
      nudgeYouTubeBootPlayback('current_video_changed');
    }
  }, [currentVideoId]);

  useEffect(() => {
    normalizeTrillboardsOverlayContainer();
  });

  useEffect(() => {
    if (!myId) {
      return;
    }

    if (isContentLoading) {
      postTrillboardsScreenVisible('content-loading', { screenId: myId });
      return;
    }

    postTrillboardsScreenVisible(currentVideoId ? 'content-shell' : 'waiting-for-content', {
      screenId: myId,
      hasVideo: Boolean(currentVideoId)
    });
  }, [currentVideoId, isContentLoading, myId]);

  useEffect(() => {
    const handleYouTubePlayerError = (event) => {
      if (event.origin !== 'https://www.youtube.com' && event.origin !== 'https://www.youtube-nocookie.com') return;
      const iframe = document.getElementById('yt-player');
      if (iframe && event.source && event.source !== iframe.contentWindow) return;
      let playerMessage = null;
      try {
        playerMessage = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (_) {}
      if (playerMessage?.event === 'readyToListen') {
        postYouTubeListening(iframe, 'trillboards-boot');
      }
      if (markYouTubePlayerReady(iframe, playerMessage)) {
        postYouTubeBootCommand(iframe, 'mute');
        postYouTubeBootCommand(iframe, 'addEventListener', ['onStateChange']);
        postYouTubeBootCommand(iframe, 'playVideo');
      }
      if (!isYouTubePlayerErrorMessage(event.data)) return;

      const errorCode = extractYouTubePlayerErrorCode(event.data);
      const shouldClearShellIframe = isBrightSignRuntime();
      logger.warn('YouTube boot iframe reported player error', {
        errorCode,
        videoId: currentVideoId,
        recoveryOwner: shouldClearShellIframe ? 'react_shell' : 'overlay_stream_manager'
      }, 'TVTestPlayer');

      // The overlay YouTubePlugin owns Android/web playback and advances the
      // stream queue on player errors. Clearing its shared iframe here races
      // that recovery and leaves the shell black. BrightSign file playback
      // still needs the original React-shell fallback added for its runtime.
      if (shouldClearShellIframe) {
        if (window.trillboardsOverlay?.streamManager?.handleYouTubePlayerError) {
          try {
            window.trillboardsOverlay.streamManager.handleYouTubePlayerError(errorCode);
          } catch (_) {}
        }

        setCurrentVideoId(null);
      }
      publishBootstrapState('youtube_shell_error', {
        errorCode,
        videoId: currentVideoId
      });
    };

    window.addEventListener('message', handleYouTubePlayerError);
    return () => window.removeEventListener('message', handleYouTubePlayerError);
  }, [currentVideoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nudgeIfVisible = (reason) => {
      if (document.visibilityState === 'hidden') return;
      nudgeYouTubeBootPlayback(reason);
    };
    const handleVisibilityChange = () => nudgeIfVisible('visibility_visible');
    const handleFocus = () => nudgeIfVisible('window_focus');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!myId) {
      return undefined;
    }
    let disposed = false;
    let flushInFlight = false;
    let flushTimerId = null;

    const isOnline = () => {
      try {
        return typeof navigator === 'undefined' || navigator.onLine !== false;
      } catch (_) {
        return true;
      }
    };

    const updateQueuedCount = (queuedEventCount = getQueuedTelemetryCount(myId)) => {
      cacheStatusDetailsRef.current = normalizeCacheStatusDetails({
        ...cacheStatusDetailsRef.current,
        queued_event_count: queuedEventCount,
      });
    };

    const scheduleFlush = (delayMs = 0) => {
      if (disposed || flushTimerId !== null) return;
      flushTimerId = window.setTimeout(() => {
        flushTimerId = null;
        flushQueue();
      }, delayMs);
    };

    const flushQueue = () => {
      if (disposed || flushInFlight || !isOnline()) {
        return;
      }
      flushInFlight = true;
      flushNativeTelemetryQueue(myId, {
        maxBatch: OFFLINE_TELEMETRY_FLUSH_BATCH_SIZE,
        maxBatches: 1,
        preferBeacon: false,
      })
        .then((nativeResult) => {
          if (Array.isArray(nativeResult.flushedEventKeys) && nativeResult.flushedEventKeys.length > 0) {
            removeQueuedTelemetryEvents(myId, nativeResult.flushedEventKeys);
          }
          return flushOfflineTelemetryQueue(myId, {
            fingerprint: myFingerPrint,
            maxBatch: OFFLINE_TELEMETRY_FLUSH_BATCH_SIZE,
            maxBatches: 1,
            preferBeacon: false,
          }).then((result) => ({ nativeResult, result }));
        })
        .then(({ nativeResult, result }) => {
          if (disposed) return;
          const queuedEventCount = getQueuedTelemetryCount(myId);
          const nativeStatus = readNativeLoopOfflineStatus(myId) || {};
          const nativeQueuedEventCount = Number(
            nativeStatus.native_queued_event_count ?? nativeResult.remaining ?? 0
          ) || 0;
          updateQueuedCount(queuedEventCount);
          if (result.flushed > 0 && queuedEventCount === 0) {
            sendBootstrapBrowserSignal(myId, 'telemetry_queue_flushed', {
              ...cacheStatusDetailsRef.current,
              reason: 'offline_queue_flushed',
              queued_event_count: 0,
            }, bootstrapSignalThrottleRef, { skipFlush: true, fingerprint: myFingerPrint });
          }
          if (nativeResult.flushed > 0) {
            sendBootstrapBrowserSignal(myId, 'native_telemetry_queue_flushed', {
              ...cacheStatusDetailsRef.current,
              reason: 'native_offline_queue_flushed',
              native_queued_event_count: nativeQueuedEventCount,
            }, bootstrapSignalThrottleRef, { skipFlush: true, fingerprint: myFingerPrint });
          }
          if ((queuedEventCount > 0 || nativeQueuedEventCount > 0) && isOnline()) {
            scheduleFlush(OFFLINE_TELEMETRY_FLUSH_RETRY_MS);
          }
        })
        .catch(() => {
          updateQueuedCount();
        })
        .finally(() => {
          flushInFlight = false;
        });
    };

    const handleOnline = () => scheduleFlush(0);

    scheduleFlush(0);
    window.addEventListener('online', handleOnline);
    return () => {
      disposed = true;
      if (flushTimerId !== null) {
        window.clearTimeout(flushTimerId);
      }
      window.removeEventListener('online', handleOnline);
    };
  }, [myFingerPrint, myId]);

  useEffect(() => {
    let timerId = null;
    const tick = () => {
      const nextState = resolveActiveLoopState(activeContentRef.current);
      try {
        window.__TRILLBOARDS_LOOP_STATE__ = nextState;
      } catch (_) {}
      setLoopPlaybackState((previousState) => {
        const sameSlot = previousState?.plan_id === nextState?.plan_id
          && previousState?.loop_manifest_source === nextState?.loop_manifest_source
          && previousState?.cycle_number === nextState?.cycle_number
          && previousState?.slot_index === nextState?.slot_index
          && previousState?.tier === nextState?.tier
          && previousState?.reservation_id === nextState?.reservation_id
          && previousState?.selected_creative_id === nextState?.selected_creative_id
          && previousState?.selected_creative_asset_url === nextState?.selected_creative_asset_url
          && previousState?.selected_creative_original_asset_url === nextState?.selected_creative_original_asset_url
          && previousState?.selected_creative_media_type === nextState?.selected_creative_media_type;
        return sameSlot ? previousState : nextState;
      });
      const delayMs = nextState ? Math.min(Math.max(nextState.remaining_ms + 75, 250), 30000) : 10000;
      timerId = setTimeout(tick, delayMs);
    };

    tick();
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [loopContentVersion]);

  useEffect(() => {
    if (!myId || !loopPlaybackState) {
      return;
    }

    // The v2 overlay owns both the visual and proof-of-play transition. Sending
    // the shell's intentionally neutral surface would create a second false
    // proof row (`media_present: false`) for the same slot occurrence.
    if (loopPlaybackState.loop_manifest_source === 'resolved_loop_plan_v2') {
      return;
    }

    const selectedCreativeMedia = resolveSelectedCreativeMedia(loopPlaybackState);
    const renderedSource = resolveRenderedLoopSource(loopPlaybackState, currentVideoId, shellMedia, selectedCreativeMedia);
    sendLoopPlaybackBrowserSignal(myId, loopPlaybackState, renderedSource, loopPlaybackSignalRef, myFingerPrint, cacheStatusDetailsRef.current, { fingerprint: myFingerPrint });
  }, [currentVideoId, loopPlaybackState, myFingerPrint, myId, shellMedia]);

  // Content monitoring functions for automatic FirstTimeGuide transitions
  const startContentMonitoring = () => {
    if (contentMonitoringRef.current) {
      clearInterval(contentMonitoringRef.current);
    }

    logger.info('Starting automatic content monitoring for seamless transitions', null, 'TVTestPlayer');

    contentMonitoringRef.current = setInterval(async () => {
      try {
        if (!isMountedRef.current) {
          return;
        }

        logger.debug('Checking for content availability', null, 'TVTestPlayer');

        const headers = {};
        if (cachedETagRef.current) {
          headers['If-None-Match'] = cachedETagRef.current;
        }

        const response = await fetchWithTimeout(
          `${resolveTrillboardsApiBase()}/v2/earner/alot-advertisement-list/${myId}`,
          { headers },
          CONTENT_MONITORING_TIMEOUT_MS
        );

        if (response.status === 304) {
          return; // Content unchanged
        }

        if (!isMountedRef.current) {
          return;
        }

        if (response.status === 404) {
          let data = null;
          try {
            data = await response.json();
          } catch (_) {}
          clearStaleScreenIdentity('content_monitoring_screen_not_found', {
            status: response.status,
            bodyCode: data?.code,
          });
          return;
        }

        const etag = getResponseETag(response);
        if (etag) {
          cachedETagRef.current = etag;
        }
        const data = await response.json();

        if (!isMountedRef.current) {
          return;
        }

        if (isHardScreenNotFoundResponse(response, data)) {
          clearStaleScreenIdentity('content_monitoring_screen_not_found', {
            status: response.status,
            bodyCode: data?.code,
          });
          return;
        }

        if (data?.status && data?.data) {
          const availability = getContentAvailability(data.data);

          if (availability.hasDisplayableContent || availability.hasProgrammatic || availability.hasLoopComposer) {
            logger.info('Content became available - transitioning to content render', null, 'TVTestPlayer');
            await persistLoopManifest(data, etag, `${resolveTrillboardsApiBase()}/v2/earner/alot-advertisement-list/${myId}`);
            applyContentToPlayer(data.data, 'content_ready_from_monitoring', {
              reason: 'monitoring_content_ready',
              using_cached_manifest: false,
            });
          }
        } else {
          logger.warn('Content monitoring response unusable', {
            status: response?.status,
            ok: response?.ok,
            bodyStatus: data?.status
          }, 'TVTestPlayer');
        }
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }

        logger.warn('Content monitoring check failed', { error: error?.message }, 'TVTestPlayer');
        await hydrateFromCachedLoopManifest('monitoring_content_check_failed', { error: error?.message });
      }
    }, 10000); // Check every 10 seconds for new content
  };

  const stopContentMonitoring = () => {
    if (contentMonitoringRef.current) {
      logger.info('Stopping content monitoring - content is now available', null, 'TVTestPlayer');
      clearInterval(contentMonitoringRef.current);
      contentMonitoringRef.current = null;
    }
  };

  // Set initial video ID and set up stream management integration. This is
  // keyed off screen/content transitions, not helper identity.
  useEffect(() => {
    if (myId) {
      logger.info('Setting up for stream management with screen', { screenId: myId }, 'TVTestPlayer');

      // Check if we have prefetched content for instant display
      if (prefetchedContent?.content) {
        const { content } = prefetchedContent;
        const availability = getContentAvailability(content);

        if (availability.hasDisplayableContent || availability.hasLoopComposer) {
          logger.info('Using prefetched content for instant display', availability, 'TVTestPlayer');
          setIsContentLoading(false);
          const shellPlayback = resolveShellPlayback(content, 'prefetched-ready');
          setCurrentStartSeconds(shellPlayback.startSeconds);
          setCurrentVideoId(shellPlayback.videoId);
          setShellMedia(shellPlayback.videoId ? null : resolveInitialShellMedia(content));

          // Make content available globally for overlay system IMMEDIATELY
          window.__PREFETCHED_CONTENT__ = content;
          window.__CONTENT_PREFETCHED_AT__ = prefetchedContent.timestamp;
          rememberActiveContent(content);
          dismissFirstTimeGuideForContent('prefetched_content_ready', availability);
          const mediaCacheStatus = getLoopMediaCacheStatus(myId, content) || {};
          publishBootstrapState('content_ready', {
            ...availability,
            reason: 'prefetched_content_ready',
            using_cached_manifest: false,
            cached_asset_count: mediaCacheStatus.cachedAssetCount,
            required_asset_count: mediaCacheStatus.requiredAssetCount,
            missing_asset_count: mediaCacheStatus.missingAssetCount,
          });
          persistLoopManifest({ status: true, data: content }, null, 'prefetched_content').catch(() => {});
          prefetchLoopMediaForContent(content, 'content_ready', {
            reason: 'prefetched_content_ready',
            using_cached_manifest: false,
          }).catch(() => {});

          // Set up global function for MinimalStreamManager to update video ID
          window.updateTVTestPlayerVideo = (videoId, startSeconds) => {
            logger.info('Received video update from MinimalStreamManager', { videoId, startSeconds }, 'TVTestPlayer');
            const nextStartSeconds = normalizeStartSeconds(startSeconds);
            setCurrentStartSeconds((previousStartSeconds) =>
              normalizeStartSeconds(previousStartSeconds) === nextStartSeconds ? previousStartSeconds : nextStartSeconds
            );
            setCurrentVideoId((previousVideoId) => (previousVideoId === videoId ? previousVideoId : videoId));
            stopContentMonitoring();
          };
          if (availability.hasLoopComposer && !availability.hasDisplayableContent && !availability.hasProgrammatic) {
            startContentMonitoring();
          }
          return; // Skip API fetch - we have content ready
        } else {
          logger.info('Prefetched content has no immediate displayable media - waiting for guide or future content', availability, 'TVTestPlayer');
          setIsContentLoading(false);
          startContentMonitoring();
        }
      }

      // Fallback: Enhanced content detection with automatic transitions
      const checkForContentWithTransitions = async () => {
        publishBootstrapState('loading_content');
        const contentEndpoint = `${resolveTrillboardsApiBase()}/v2/earner/alot-advertisement-list/${myId}`;
        try {
          const headers = {};
          if (cachedETagRef.current) {
            headers['If-None-Match'] = cachedETagRef.current;
          }

          const response = await fetchWithTimeout(
            contentEndpoint,
            { headers },
            CONTENT_BOOTSTRAP_TIMEOUT_MS
          );

          if (!isMountedRef.current) {
            return;
          }

          if (response.status === 304) {
            if (await hydrateFromCachedLoopManifest('not_modified')) {
              return;
            }
            setIsContentLoading(false);
            publishBootstrapState('shell_polling', { reason: 'not_modified' });
            startContentMonitoring();
            return; // Content unchanged
          }

          if (response.status === 404) {
            let data = null;
            try {
              data = await response.json();
            } catch (_) {}
            clearStaleScreenIdentity('content_bootstrap_screen_not_found', {
              status: response.status,
              bodyCode: data?.code,
            });
            return;
          }

          const etag = getResponseETag(response);
          if (etag) {
            cachedETagRef.current = etag;
          }
          const data = await response.json();

          if (!isMountedRef.current) {
            return;
          }

          if (isHardScreenNotFoundResponse(response, data)) {
            clearStaleScreenIdentity('content_bootstrap_screen_not_found', {
              status: response.status,
              bodyCode: data?.code,
            });
            return;
          }

          if (data?.status && data?.data) {
            const availability = getContentAvailability(data.data);

            if (!availability.hasDisplayableContent && !availability.hasLoopComposer) {
              logger.info('No immediate displayable content found - waiting for guide or future content', availability, 'TVTestPlayer');
              setIsContentLoading(false);
              publishBootstrapState('shell_polling', { reason: 'empty_content' });

              // Set up periodic content monitoring for automatic transitions
              startContentMonitoring();
              return;
            } else {
              logger.info('Displayable content detected', availability, 'TVTestPlayer');
              await persistLoopManifest(data, etag, contentEndpoint);
              applyContentToPlayer(data.data, 'content_ready', {
                reason: 'bootstrap_content_ready',
                using_cached_manifest: false,
              });
            }
          } else {
            logger.warn('Content bootstrap response unusable - mounting shell and polling', {
              status: response?.status,
              ok: response?.ok,
              bodyStatus: data?.status
            }, 'TVTestPlayer');
            if (await hydrateFromCachedLoopManifest('unusable_response', {
              status: response?.status,
              bodyStatus: data?.status
            })) {
              return;
            }
            setIsContentLoading(false);
            publishBootstrapState('shell_polling', {
              reason: 'unusable_response',
              status: response?.status,
              bodyStatus: data?.status
            });
            startContentMonitoring();
          }
        } catch (error) {
          if (!isMountedRef.current) {
            return;
          }

          logger.warn('Content check failed - mounting shell and polling', { error: error?.message }, 'TVTestPlayer');
          if (await hydrateFromCachedLoopManifest('content_check_failed', { error: error?.message })) {
            return;
          }
          setIsContentLoading(false);
          publishBootstrapState('shell_polling', { reason: 'content_check_failed', error: error?.message });
          startContentMonitoring();
        }
      };

      // Only call API if no prefetched content
      if (!prefetchedContent?.content) {
        checkForContentWithTransitions();
      }

      // Set up global function for MinimalStreamManager to update video ID
      window.updateTVTestPlayerVideo = (videoId, startSeconds) => {
        logger.info('Received video update from MinimalStreamManager', { videoId, startSeconds }, 'TVTestPlayer');
        const nextStartSeconds = normalizeStartSeconds(startSeconds);
        setCurrentStartSeconds((previousStartSeconds) =>
          normalizeStartSeconds(previousStartSeconds) === nextStartSeconds ? previousStartSeconds : nextStartSeconds
        );
        setCurrentVideoId((previousVideoId) => (previousVideoId === videoId ? previousVideoId : videoId));
        if (videoId) {
          setShellMedia(null);
        }
        stopContentMonitoring(); // Stop monitoring since we have content
      };
    }
  }, [myId, prefetchedContent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for stream manager updates via window message or direct calls
  useEffect(() => {
    const handleStreamUpdate = (event) => {
      if (event.data && event.data.type === 'STREAM_UPDATE' && event.data.videoId) {
        logger.info('Received stream update message', { videoId: event.data.videoId }, 'TVTestPlayer');
        setCurrentVideoId((previousVideoId) =>
          previousVideoId === event.data.videoId ? previousVideoId : event.data.videoId
        );
      }
    };

    window.addEventListener('message', handleStreamUpdate);
    
    return () => {
      window.removeEventListener('message', handleStreamUpdate);
      // Clean up global function
      if (window.updateTVTestPlayerVideo) {
        delete window.updateTVTestPlayerVideo;
      }
    };
  }, []);

  // Load overlay system when component mounts
  useEffect(() => {
    if (myId && !overlayLoaded) {
      logger.info('Loading overlay system for screen ID', { screenId: myId }, 'TVTestPlayer');
      const overlayVersion = process.env.REACT_APP_VERSION || 'dev';

      // Disconnect React's socket connection to prevent conflicts with vanilla overlay
      if (window.reactSocketInstance && typeof window.reactSocketInstance.disconnect === 'function') {
        logger.debug('Disconnecting React socket connection', null, 'TVTestPlayer');
        window.reactSocketInstance.disconnect();
      }

      // Make myId and myFingerPrint globally accessible for the overlay
      window.myId = myId;
      window.__TRILLBOARD_SCREEN_ID__ = myId;
      window.myFingerPrint = myFingerPrint;
      // Ensure screenId is in localStorage for PostHog error telemetry
      try {
        localStorage.setItem('screenId', myId);
        if (myFingerPrint) {
          localStorage.setItem('trillboard_screen_identity_fingerprint', myFingerPrint);
        }
      } catch (e) { /* ignore */ }

      // CRITICAL: Expose prefetched content to overlay BEFORE loading script
      // This ensures overlay can use it immediately on initialization
      if (prefetchedContent?.content) {
        window.__PREFETCHED_CONTENT__ = prefetchedContent.content;
        window.__CONTENT_PREFETCHED_AT__ = prefetchedContent.timestamp;
        rememberActiveContent(prefetchedContent.content);
        logger.info('Prefetched content exposed to overlay before script load', {
          hasStreams: prefetchedContent.content.all_default_streams?.length > 0,
          hasAds: prefetchedContent.content.overlay_advertisements?.length > 0
        }, 'TVTestPlayer');
      }

      const initializeOverlay = () => {
        if (window.TrillboardsOverlay && typeof window.TrillboardsOverlay.init === 'function') {
          window.TrillboardsOverlay.init(myId);
        } else if (typeof window.initMinimalOverlay === 'function') {
          window.initMinimalOverlay(myId);
        }
      };

      let existingScript = document.getElementById('trillboards-overlay-script');
      if (existingScript?.dataset.loadFailed === 'true') {
        logger.warn('Discarding failed overlay preload before playback initialization', null, 'TVTestPlayer');
        existingScript.remove();
        existingScript = null;
      }
      if (existingScript) {
        if (existingScript.dataset.loaded === 'true' || window.TrillboardsOverlay) {
          logger.info('Overlay system already loaded', null, 'TVTestPlayer');
          if (shouldDismissFirstTimeGuideForContent(window.__PREFETCHED_CONTENT__)) {
            dismissFirstTimeGuideForContent('overlay_already_loaded_with_content');
          }
          if (window.__TRILL_PLATFORM__ === 'brightsign') initializeOverlay();
          setOverlayLoaded(true);
          return undefined;
        }

        const handleLoad = () => {
          existingScript.dataset.loaded = 'true';
          logger.info('Overlay system loaded from existing script', null, 'TVTestPlayer');
          if (shouldDismissFirstTimeGuideForContent(window.__PREFETCHED_CONTENT__)) {
            dismissFirstTimeGuideForContent('existing_overlay_loaded_with_content');
          }
          if (window.__TRILL_PLATFORM__ === 'brightsign') initializeOverlay();
          setOverlayLoaded(true);
        };
        const handleError = () => {
          existingScript.dataset.loadFailed = 'true';
          existingScript.remove();
          logger.error('Failed to load overlay system', null, 'TVTestPlayer');
        };

        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', handleError, { once: true });
        return () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
        };
      }

      const script = document.createElement('script');
      script.id = 'trillboards-overlay-script';
      script.src = `${resolveRuntimeAssetUrl('trillboards-overlay.js')}?v=${encodeURIComponent(overlayVersion)}`;
      script.async = true;
      script.dataset.loaded = 'false';
      script.onload = () => {
        script.dataset.loaded = 'true';
        logger.info('Overlay system loaded', null, 'TVTestPlayer');
        if (shouldDismissFirstTimeGuideForContent(window.__PREFETCHED_CONTENT__)) {
          dismissFirstTimeGuideForContent('overlay_loaded_with_content');
        }
        if (window.__TRILL_PLATFORM__ === 'brightsign') initializeOverlay();
        setOverlayLoaded(true);

        // Overlay will auto-initialize with myId from window and use __PREFETCHED_CONTENT__
      };
      script.onerror = () => {
        script.dataset.loadFailed = 'true';
        script.remove();
        logger.error('Failed to load overlay system', null, 'TVTestPlayer');
      };
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [myFingerPrint, myId, overlayLoaded, prefetchedContent]);

  // Initialize HealthWatchdog for auto-recovery
  useEffect(() => {
    if (overlayLoaded && !healthWatchdogRef.current) {
      logger.info('Initializing HealthWatchdog for auto-recovery', null, 'TVTestPlayer');

      try {
        // Create HealthMonitor instance
        const healthMonitor = new HealthMonitor();

        // Create and start HealthWatchdog
        healthWatchdogRef.current = new HealthWatchdog(healthMonitor);
        healthWatchdogRef.current.start();

        // Expose watchdog status on window for debugging
        window.healthWatchdogStatus = () => healthWatchdogRef.current?.getStatus();

        logger.info('HealthWatchdog started successfully', null, 'TVTestPlayer');
      } catch (error) {
        logger.error('Failed to initialize HealthWatchdog', { error: error?.message }, 'TVTestPlayer');
      }
    }

    return () => {
      if (healthWatchdogRef.current) {
        healthWatchdogRef.current.stop();
        healthWatchdogRef.current = null;
        delete window.healthWatchdogStatus;
      }
    };
  }, [overlayLoaded]);

  // Stop content polling when the component unmounts.
  useEffect(() => {
    return () => {
      stopContentMonitoring();
    };
  }, []);

  useEffect(() => {
    const heartbeatState = currentVideoId
      ? 'content-shell'
      : (isContentLoading ? 'content-loading' : 'waiting-for-content');
    postTrillboardsScreenHeartbeat(heartbeatState, {
      screenId: myId || null,
      hasVideo: Boolean(currentVideoId),
    });

    const heartbeatTimer = setInterval(() => {
      postTrillboardsScreenHeartbeat(heartbeatState, {
        screenId: myId || null,
        hasVideo: Boolean(currentVideoId),
      });
    }, 15000);

    return () => clearInterval(heartbeatTimer);
  }, [currentVideoId, isContentLoading, myId]);

  // Video ID is now updated directly from API data, no need for message listener

  // Show loading state if no video is available
  if (!currentVideoId) {
    if (!isContentLoading) {
      // Keep overlay surfaces mounted even when there is no background stream.
      const showWaitingForContent = !shouldDismissFirstTimeGuideForContent(activeContentRef.current);
      return renderContentShell({
        currentVideoId: null,
        shellMedia,
        loopPlaybackState,
        showWaitingForContent,
      });
    }

    return renderBrandedStatus({
      screenId: myId,
      title: 'Preparing Your Display',
      subtitle: `This ${getPairedDeviceLabel(navigator.userAgent)} is paired. Trillboards is loading the scheduled content and will start playback automatically.`
    });
  }

  return renderContentShell({ currentVideoId, currentStartSeconds, shellMedia, loopPlaybackState });
};

export default TVTestPlayer;
